/**
 * Money formatting.
 *
 * Every amount crosses the API as an integer in the currency's minor unit, paired with an ISO 4217 code from
 * the location. The legacy app sent both a number and a pre-formatted string for every amount; this app
 * formats client-side instead, so the server never has to guess a locale and there is exactly one
 * representation of an amount on the wire.
 *
 * The minor-unit divisor is not always 100. JPY has none, and several currencies use three. `Intl` knows the
 * right exponent per currency, so it is asked rather than assumed.
 */

/** Cached because constructing a NumberFormat is expensive and a check re-renders on every tip change. */
const formatterCache = new Map<string, Intl.NumberFormat>();

function formatter(locale: string, currency: string): Intl.NumberFormat {
    const key = `${locale}|${currency}`;
    const cached = formatterCache.get(key);
    if (cached) return cached;

    const created = new Intl.NumberFormat(locale, { style: 'currency', currency });
    formatterCache.set(key, created);
    return created;
}

/**
 * How many minor units make one major unit for this currency — 100 for USD, 1 for JPY, 1000 for KWD.
 *
 * Derived from `Intl`'s own fraction-digit rules rather than a hardcoded table, so a currency we have never
 * seen formats correctly instead of being silently divided by 100.
 */
export function minorUnitsPerMajor(currency: string): number {
    const digits = new Intl.NumberFormat('en-US', { style: 'currency', currency })
        .resolvedOptions().maximumFractionDigits ?? 2;

    return 10 ** digits;
}

/**
 * Formats an integer amount of minor units as currency.
 *
 * @param minorUnits cents for USD, yen for JPY — always an integer
 * @param currency ISO 4217, from the location
 * @param locale BCP 47, from the location — decides separators and symbol placement, not the currency
 */
export function formatMoney(minorUnits: number, currency: string, locale: string): string {
    return formatter(locale, currency).format(minorUnits / minorUnitsPerMajor(currency));
}

/** The characters this locale uses to group thousands and to mark the decimal point. */
function separators(locale: string): { group: string; decimal: string } {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);

    return {
        group: parts.find((part) => part.type === 'group')?.value ?? ',',
        decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    };
}

/**
 * Turns what a guest typed into an integer number of minor units, or null if it is not an amount.
 *
 * This is the only place in the app that converts a human's decimal into money, and it is deliberately done
 * on the string rather than with arithmetic: `12.10 * 100` is 1209.9999999999998 in IEEE 754, and floor()ing
 * that undercharges by a cent. Composing the integer from the digits cannot drift.
 *
 * Separators are read from the LOCATION's locale, since the amount belongs to the same check as every other
 * figure on screen. Two allowances on top of that, both aimed at a real phone:
 *
 *  - The locale's group separator is always discarded, so "1,234" reads as 1234 in en-US.
 *  - The other separator is accepted as a decimal point when the locale's own is absent and it is followed by
 *    no more digits than the currency has - a guest in Spain whose numeric keypad offers "." rather than ","
 *    types "12.50" and means 12,50. "1.234" in the same locale still reads as 1234, because three digits is
 *    more fraction than a euro has.
 *
 * Extra fraction digits round rather than truncate, so "12.999" is 13.00 and not 12.99.
 */
export function parseMoneyInput(text: string, currency: string, locale: string): number | null {
    const trimmed = text.trim();

    // A negative tip is not a thing, and a minus sign would otherwise be silently stripped below.
    if (!trimmed || trimmed.includes('-')) return null;

    const { group, decimal } = separators(locale);
    const digits = Math.round(Math.log10(minorUnitsPerMajor(currency)));

    // Anything that might be separating digits: this locale's two, plus the pair a keypad offers regardless.
    const candidates = new Set([group, decimal, '.', ',']);

    // Keep digits and separators; drop currency symbols, spaces and the non-breaking spaces some locales group with.
    let working = '';
    for (const character of trimmed) {
        if (/\d/.test(character) || candidates.has(character)) working += character;
    }
    if (!working) return null;

    const found = [...working].filter((character) => candidates.has(character));
    const distinct = new Set(found);
    const occurrences = (character: string) => found.filter((each) => each === character).length;

    /*
     * Which separator, if any, is the decimal point. Classified before anything is removed - stripping the
     * locale's group character first would read "12.50" in es-ES as 1250 euros rather than 12,50.
     */
    let decimalCharacter: string | null = null;

    if (distinct.has(decimal)) {
        decimalCharacter = decimal;
    } else if (distinct.size === 1) {
        const only = [...distinct][0];
        const tail = working.slice(working.lastIndexOf(only) + 1);

        // One separator, and too few digits after it to be grouping: the guest meant a decimal point.
        if (occurrences(only) === 1 && digits > 0 && tail.length <= digits) decimalCharacter = only;
    } else if (distinct.size > 1) {
        // Mixed separators, none of them this locale's: by convention the last one is the decimal point.
        decimalCharacter = found[found.length - 1];
    }

    // A decimal point appears once. Twice means we are not looking at a number.
    if (decimalCharacter && occurrences(decimalCharacter) > 1) return null;

    // Whatever is left over may only be grouping, and grouping is the group character.
    for (const character of distinct) {
        if (character !== decimalCharacter && character !== group) return null;
    }

    let normalized = '';
    for (const character of working) {
        if (!candidates.has(character)) normalized += character;
        else if (character === decimalCharacter) normalized += '.';
    }

    const pieces = normalized.split('.');
    if (pieces.length > 2) return null;

    const [whole = '', fraction = ''] = pieces;

    // Reject anything left that is not a digit — a stray symbol means we misread the input, not that it is 0.
    if (!/^\d*$/.test(whole) || !/^\d*$/.test(fraction)) return null;
    if (!whole && !fraction) return null;

    // A dozen digits of major units is far past any real check and past exact integer arithmetic.
    if (whole.length > 12) return null;

    const kept = fraction.slice(0, digits).padEnd(digits, '0');
    const dropped = fraction.slice(digits);

    let minorUnits = Number(whole || '0') * 10 ** digits + Number(kept || '0');
    if (dropped && Number(dropped[0]) >= 5) minorUnits += 1;

    return Number.isSafeInteger(minorUnits) ? minorUnits : null;
}

/** Cached alongside the currency formatters, for the same reason: tip labels re-render on every selection. */
const percentFormatterCache = new Map<string, Intl.NumberFormat>();

/**
 * Renders a tip option's percentage for its label — 20 as "20%".
 *
 * Lives here beside the money formatting because it labels an amount and has to read as part of the same
 * figure. It exists ONLY for the label: the amount charged comes from the server, and nothing in this app
 * multiplies a check by this percentage. See `TipOption`.
 *
 * Whole percent, matching the contract — the POS configuration stores an integer, so 18.5% is not expressible
 * anywhere upstream. `maximumFractionDigits` stays at 2 so that if it ever becomes expressible, the label
 * follows without a change here.
 */
export function formatPercent(percentage: number, locale: string): string {
    let formatter = percentFormatterCache.get(locale);

    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 });
        percentFormatterCache.set(locale, formatter);
    }

    return formatter.format(percentage / 100);
}

/**
 * Writes an integer amount back out as digits a guest could have typed.
 *
 * The inverse of `parseMoneyInput`, and used for exactly one thing: restoring a custom tip into the input
 * when a guest navigates back to it. Round-tripping matters more than prettiness, so this emits bare digits
 * and this location's decimal separator - no grouping and no currency symbol - which `parseMoneyInput` reads
 * back as the same integer it started from.
 */
export function toInputText(minorUnits: number, currency: string, locale: string): string {
    const digits = Math.round(Math.log10(minorUnitsPerMajor(currency)));
    const divisor = 10 ** digits;

    const whole = Math.trunc(minorUnits / divisor);
    if (digits === 0) return String(whole);

    const fraction = String(minorUnits % divisor).padStart(digits, '0');

    return String(whole) + separators(locale).decimal + fraction;
}
